import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import WarningOutlined from '@ant-design/icons/WarningOutlined';

/**
 * AbhaMismatchDialog
 *
 * Shown when the verified ABHA profile's demographics do not closely match
 * the locally-selected patient.  The receptionist can either:
 *   - Cancel (safe default) — go back and try the correct identifier
 *   - Link Anyway — explicit override after reviewing both sides
 */
export default function AbhaMismatchDialog({ open, localPatient, abhaProfile, onLinkAnyway, onCancel }) {
  const abhaFullName =
    [abhaProfile?.firstName, abhaProfile?.middleName, abhaProfile?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() || '—';

  const abhaDob = abhaProfile?.dateOfBirth?.format
    ? abhaProfile.dateOfBirth.format('YYYY-MM-DD')
    : abhaProfile?.dateOfBirth
    ? String(abhaProfile.dateOfBirth)
    : '—';

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <WarningOutlined style={{ color: '#ff9800', fontSize: 22 }} />
        <Typography variant="h5" component="span">
          Patient Identity Mismatch
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          The ABHA profile you verified{' '}
          <strong>does not appear to match</strong> the patient currently selected in the
          appointment form. Review both records carefully before proceeding.
        </Alert>

        <Table size="small" sx={{ mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: '28%' }}>Field</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                Selected Patient{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  (Local Record)
                </Typography>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                Verified ABHA{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  (ABDM)
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>{localPatient?.full_name || '—'}</TableCell>
              <TableCell sx={{ color: 'warning.dark', fontWeight: 600 }}>{abhaFullName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Gender</TableCell>
              <TableCell>{localPatient?.gender || '—'}</TableCell>
              <TableCell sx={{ color: 'warning.dark', fontWeight: 600 }}>
                {abhaProfile?.gender || '—'}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Date of Birth</TableCell>
              <TableCell>{localPatient?.dob || '—'}</TableCell>
              <TableCell sx={{ color: 'warning.dark', fontWeight: 600 }}>{abhaDob}</TableCell>
            </TableRow>
            {abhaProfile?.abhaNumber && (
              <TableRow>
                <TableCell>ABHA Number</TableCell>
                <TableCell>—</TableCell>
                <TableCell>{abhaProfile.abhaNumber}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.lighter', borderRadius: 1 }}>
          <Typography variant="body2" color="warning.dark">
            <strong>If you click "Link Anyway"</strong>, this ABHA will be attached to the
            selected local patient and their demographic record may be updated to match the ABHA
            data. Only proceed if you are certain this is the same person.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} color="inherit" variant="outlined">
          Cancel — Try Again
        </Button>
        <Button onClick={onLinkAnyway} color="warning" variant="contained">
          Link Anyway — I Confirm This Is The Same Person
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AbhaMismatchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  localPatient: PropTypes.shape({
    full_name: PropTypes.string,
    gender: PropTypes.string,
    dob: PropTypes.string,
  }),
  abhaProfile: PropTypes.shape({
    firstName: PropTypes.string,
    middleName: PropTypes.string,
    lastName: PropTypes.string,
    gender: PropTypes.string,
    dateOfBirth: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    abhaNumber: PropTypes.string,
  }),
  onLinkAnyway: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
